import React, { useState, useEffect, useRef } from "react";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import UpdateIcon from "@mui/icons-material/Update";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";

interface Props {
  lastUpdated?: string;
  idProyecto?: string;
}

export default function UpdateMGMT({ lastUpdated, idProyecto }: Props) {
  const queryClient = useQueryClient();
  const hasFetchedRef = useRef(false);
  const initialLoadTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (initialLoadTimeoutRef.current) {
      clearTimeout(initialLoadTimeoutRef.current);
    }

    return () => {
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current);
      }
    };
  }, []);

  const shouldShowSkeleton = !lastUpdated;

  useEffect(() => {
    if (idProyecto && !hasFetchedRef.current) {
      queryClient.invalidateQueries({ queryKey: ["proyecto", idProyecto] });
      hasFetchedRef.current = true;
    }
  }, [idProyecto, queryClient]);

  const formatDate = (timestamp?: string) => {
    if (!timestamp) return "Sin actualizar";

    const fecha = new Date(timestamp);
    if (isNaN(fecha.getTime())) return "Sin actualizar";

    const ahora = new Date();
    const fechaLocal = (d: Date) => d.toDateString();
    const esHoy = fechaLocal(fecha) === fechaLocal(ahora);

    const ayer = new Date(ahora);
    ayer.setDate(ahora.getDate() - 1);
    const esAyer = fechaLocal(fecha) === fechaLocal(ayer);

    const hora = fecha.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    if (esHoy) return `Hoy, ${hora}`;
    if (esAyer) return `Ayer, ${hora}`;
    
    const fechaFormateada = fecha.toLocaleDateString("es-AR", {
      day: "numeric",
      month: "short",
    });
    return `${fechaFormateada}, ${hora}`;
  };

  const getNextUpdateTime = (timestamp?: string) => {
    if (!timestamp) return "Sin información";

    const fecha = new Date(timestamp);
    if (isNaN(fecha.getTime())) return "Sin información";

    const proxima = new Date(fecha.getTime() + 30 * 60 * 1000);
    return formatDate(proxima.toISOString());
  };

  const getTimeRemaining = (timestamp?: string) => {
    if (!timestamp) return "";

    const fecha = new Date(timestamp);
    if (isNaN(fecha.getTime())) return "";

    const proxima = new Date(fecha.getTime() + 30 * 60 * 1000);
    const ahora = new Date();
    const diferencia = proxima.getTime() - ahora.getTime();
     
    if (diferencia <= 0) return;
  };

  const getUpdateProgress = () => {
    if (!lastUpdated) return 0;
    
    const fecha = new Date(lastUpdated);
    if (isNaN(fecha.getTime())) return 0;
    
    const ahora = new Date();
    const tiempoTranscurrido = ahora.getTime() - fecha.getTime();
    const porcentaje = (tiempoTranscurrido / (30 * 60 * 1000)) * 100;
    
    return Math.min(100, Math.max(0, porcentaje));
  };

  const formattedLastUpdated = formatDate(lastUpdated);
  const formattedNextUpdate = getNextUpdateTime(lastUpdated);
  const timeRemaining = getTimeRemaining(lastUpdated);
  const updateProgress = getUpdateProgress();

  // Estado de carga (skeleton)
  if (shouldShowSkeleton) {
    return (
      <>
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} sx={{ color: "primary.main" }} />
          <Box>
            <Skeleton variant="text" width={120} height={16} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width={80} height={20} />
          </Box>
        </Box>

        <Box 
          sx={{ 
            width: "1px", 
            height: "32px", 
            backgroundColor: "divider" 
          }} 
        />

        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} sx={{ color: "primary.main" }} />
          <Box>
            <Skeleton variant="text" width={120} height={16} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width={80} height={20} />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* Última actualización */}
      <Box display="flex" alignItems="center" gap={1}>
        <AccessTimeIcon sx={{ fontSize: 18, color: "success.main" }} />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Última actualización
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {formattedLastUpdated}
          </Typography>
        </Box>
      </Box>

      {/* Separador */}
      <Box 
        sx={{ 
          width: "1px", 
          height: "32px", 
          backgroundColor: "divider" 
        }} 
      />

      {/* Próxima actualización */}
      <Box display="flex" alignItems="center" gap={1}>
        <UpdateIcon sx={{ fontSize: 18, color: "info.main" }} />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Próxima {timeRemaining && (
              <Typography 
                component="span" 
                variant="caption" 
                sx={{ 
                  color: updateProgress > 90 ? "warning.main" : "success.main",
                  fontWeight: 500,
                  ml: 0.5
                }}
              >
                {timeRemaining}
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {formattedNextUpdate}
          </Typography>
        </Box>
      </Box>
    </>
  );
}